<script setup lang="ts">
/**
 * AdministerFeedDialog
 *
 * Scan-and-verify dialog for Feed Administration tab.
 * Matches Figma node 5731:149114.
 *
 * Step 1: Scan wristband (patient name) + Feed ID / Container Barcode.
 * Emits "verified" with the resolved values on success.
 */
import { ref, computed, watch } from 'vue';
import { useFormDirty } from '@/shared/composables/useFormDirty';
import FormDirtyDialog from '@/shared/components/feedback/FormDirtyDialog.vue';
import { isAxiosError } from 'axios';
import { sanitizeMessage } from '@/core/utils/api-error.util';
import Button from 'primevue/button';
import { DialogModal, useToaster } from '@tms-phoenix/ui-vue';
import { useBarcodeScanner } from '@/shared/composables/useBarcodeScanner';
import ScanInput from '@/shared/components/ui/ScanInput.vue';
import { useLocale } from '@/shared/composables/useLocale';
import { isUuid } from '@/shared/utils/uuid';
import { logAuditEvent } from '@/core/services/audit.service';
import { administerFeed } from '../services/feed-administration.api';
import { useFeedAdministrationStore } from '@/stores/feed-administration.store';
defineOptions({ inheritAttrs: false });

// ── Props & Emits ──────────────────────────────────────────────────────────
interface Props {
  visible: boolean;
  initialMrn?: string;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void;
  (e: 'verified', payload: { patientName: string; feedId: string }): void;
}>();

const toast = useToaster();
const { t } = useLocale();
const feedAdminStore = useFeedAdministrationStore();

// Load feed-administration records when the dialog opens.
watch(
  () => props.visible,
  (open) => {
    if (open) {
      if (feedAdminStore.activeTab !== 'feed-administration') {
        feedAdminStore.setTab('feed-administration');
      }
      if (props.initialMrn) {
        mrn.value = props.initialMrn;
      }
      void _scanner.connectScanner();
    } else {
      _scanner.disconnectScanner();
    }
  },
);

// ── Form state ─────────────────────────────────────────────────────────────
const mrn = ref('');
const feedId = ref('');
const isVerifying = ref(false);

/** Patient resolved from the patient store after a successful Search. */
const scannedPatient = ref<{ id: string; name: string } | null>(null);

/** Resolve patient from the typed MRN. */
const resolvedPatient = computed(() =>
  feedAdminStore.records.find((r) => r.mrn === mrn.value.trim()) ?? null
);

const selectedPatientId = computed(
  () => scannedPatient.value?.id || resolvedPatient.value?.patientId || ''
);
const patientName = computed(
  () => scannedPatient.value?.name || resolvedPatient.value?.patientName || ''
);

function resetForm() {
  mrn.value = '';
  feedId.value = '';
  isVerifying.value = false;
  scannedPatient.value = null;
}

const { markDirty, reset: resetDirty, guardClose, confirmState, onConfirm, onCancel, onVisibleUpdate } = useFormDirty();
watch([mrn, feedId], () => markDirty());

// ── Barcode scanner (SSE) ────────────────────────────────────────────────────
const _scanner = useBarcodeScanner({ workflow: 'administer-feed' });

// Both fields pass barcode through unchanged — no post-decode side-effect needed.
const mrnScanner = _scanner;
const feedIdScanner = _scanner;

async function handleClose() {
  if (!(await guardClose())) return;
  resetForm();
  nextTick(() => resetDirty());
  emit('update:visible', false);
}

/** True only when both required fields are filled. */
const isFormValid = computed(
  () => mrn.value.trim().length > 0 && feedId.value.trim().length > 0,
);

async function handleVerify() {
  if (!isFormValid.value) return;
  isVerifying.value = true;

  let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
  let auditResourceId: string | undefined;
  let auditErrorCode: string | undefined;
  let auditErrorMessage: string | undefined;
  let auditNewValue: unknown | undefined;

  try {
    const input = mrn.value.trim();
    const resolvedId = selectedPatientId.value || (isUuid(input) ? input : undefined);

    const requestBody = {
      ...(resolvedId ? { patient_id: resolvedId } : { mrn: input }),
      container_identifier: feedId.value.trim(),
    };
    auditNewValue = requestBody;

    const response = await administerFeed(requestBody);
    const { verification_result, safety_checks, delivery_id } = response.data;

    // Defensive: surface the first failed safety check (Phase 1 & 2 failures
    // normally return 409, but guard here in case the server returns 200 with
    // a failed check).
    if (!safety_checks.patient_link_passed) {
      auditErrorCode = 'PATIENT_LINK_CHECK_FAILED';
      auditErrorMessage = t('feedAdmin.administerDialog.toastPatientMismatch');
      toast.danger(t('feedAdmin.administerDialog.toastPatientMismatch'));
      return;
    }
    if (!safety_checks.npo_check_passed) {
      auditErrorCode = 'NPO_CHECK_FAILED';
      auditErrorMessage = t('feedAdmin.administerDialog.toastNpoActive');
      toast.danger(t('feedAdmin.administerDialog.toastNpoActive'));
      return;
    }
    if (!safety_checks.allergen_check_passed) {
      auditErrorCode = 'ALLERGEN_CHECK_FAILED';
      auditErrorMessage = t('feedAdmin.administerDialog.toastAllergenConflict');
      toast.danger(t('feedAdmin.administerDialog.toastAllergenConflict'));
      return;
    }
    if (!safety_checks.expiry_check_passed) {
      auditErrorCode = 'EXPIRY_CHECK_FAILED';
      auditErrorMessage = t('feedAdmin.administerDialog.toastProductsExpired');
      toast.danger(t('feedAdmin.administerDialog.toastProductsExpired'));
      return;
    }
    if (!safety_checks.recall_check_passed) {
      auditErrorCode = 'RECALL_CHECK_FAILED';
      auditErrorMessage = t('feedAdmin.administerDialog.toastProductsRecalled');
      toast.danger(t('feedAdmin.administerDialog.toastProductsRecalled'));
      return;
    }
    if (verification_result !== 'PASS') {
      auditErrorCode = 'VERIFICATION_FAILED';
      auditErrorMessage = t('feedAdmin.administerDialog.toastVerificationFailed');
      toast.danger(t('feedAdmin.administerDialog.toastVerificationFailed'));
      return;
    }

    auditStatus = 'SUCCESS';
    auditResourceId = delivery_id;

    toast.success(t('feedAdmin.administerDialog.toastSuccess'));
    emit('verified', { patientName: patientName.value.trim(), feedId: feedId.value.trim() });
    resetDirty();
    handleClose();
  } catch (err: unknown) {
    type ApiError = { response?: { status?: number; data?: { error?: { message?: string } } } };
    const apiErr = err as ApiError;
    const status = apiErr?.response?.status;
    const apiMessage = apiErr?.response?.data?.error?.message;

    // The administerFeed endpoint always returns a raw English message (e.g.
    // "Patient with MRN X not found in this hospital") that is never localized
    // server-side. Prefer the pre-translated, status-based toast text for the
    // known status codes so non-English locales don't leak raw API text; only
    // fall back to sanitizeMessage(apiMessage) for unexpected status codes.
    let message: string;
    if (status === 400) {
      message = t('feedAdmin.administerDialog.toastError400');
    } else if (status === 404) {
      message = t('feedAdmin.administerDialog.toastError404');
    } else if (status === 409) {
      message = t('feedAdmin.administerDialog.toastError409');
    } else if (apiMessage) {
      message = sanitizeMessage(apiMessage);
    } else {
      message = t('feedAdmin.administerDialog.toastErrorGeneric');
    }
    toast.danger(message);
    auditErrorCode = 'ADMINISTER_FEED_FAILED';
    auditErrorMessage = message;
    if (isAxiosError(err)) {
      const apiError = err.response?.data?.error;
      if (typeof apiError === 'string') {
        auditErrorCode = apiError;
      } else if (typeof apiError?.code === 'string') {
        auditErrorCode = apiError.code;
      } else {
        auditErrorCode = err.code ?? 'ADMINISTER_FEED_FAILED';
      }
    }
  } finally {
    isVerifying.value = false;
    try {
      logAuditEvent({
        eventCategory: 'FEED_ADMINISTRATION',
        eventType: 'ADMINISTER_FEED',
        eventStatus: auditStatus,
        resourceType: 'feedAdmin',
        resourceId: auditResourceId,
        isPhiAccess: true,
        newValue: auditNewValue,
        errorCode: auditErrorCode,
        errorMessage: auditErrorMessage,
      });
    } catch {
      // Audit must never block the flow
    }
  }
}
</script>

<template>
  <DialogModal
    :visible="visible"
    :title="$t('feedAdmin.administerDialog.title')"
    intercept-close
    @close="handleClose"
    @update:visible="(v) => { if (v) emit('update:visible', v); }"
  >
    <template #default>
      <div class="flex flex-col gap-6 p-1">
        <p class="text-sm text-body-subtle">
          {{ $t('feedAdmin.administerDialog.description') }}
        </p>

        <!-- Patient Name -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-heading">
            {{ $t('patient.name') }}
            <span class="text-red-500 ml-0.5">*</span>
          </label>
          <ScanInput
            v-model="mrn"
            :scanner="mrnScanner"
            :placeholder="$t('feedAdmin.administerDialog.mrnPlaceholder')"
          />
        </div>

        <!-- Feed ID / Container Barcode -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-heading">
            {{ $t('feedAdmin.administerDialog.feedIdLabel') }}
            <span class="text-red-500 ml-0.5">*</span>
          </label>
          <ScanInput
            v-model="feedId"
            :scanner="feedIdScanner"
            :autofocus="false"
            :placeholder="$t('feedAdmin.administerDialog.feedIdPlaceholder')"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex gap-3 px-5 py-4">
        <Button
          severity="primary"
          :loading="isVerifying"
          :disabled="!isFormValid"
          class="px-5 py-3 text-sm font-medium rounded-xl!"
          @click="handleVerify"
        >
          {{ $t('auth.mfa.verifyButton') }}
        </Button>
        <Button
          severity="secondary"
          outlined
          class="px-5 py-3 text-sm font-medium rounded-xl!"
          @click="handleClose"
        >
          {{ $t('common.cancel') }}
        </Button>
      </div>
    </template>
  </DialogModal>

  <FormDirtyDialog
    :visible="confirmState.visible"
    @confirm="onConfirm"
    @cancel="onCancel"
    @update:visible="onVisibleUpdate"
  />
</template>
